/**
 * Phase 5.3 — TikTok production-review readiness page.
 *
 * Aggregates the live readiness checklist + an embedded demo-recording
 * guide + a copy-pasteable submission packet (scope justifications,
 * privacy/terms URLs, demo video URL) so Matt can submit for production
 * review without leaving the app.
 *
 * Backend powers everything via GET /api/settings/review-readiness; the
 * recording flow + voiceover script + scope text live server-side so
 * policy updates ship via redeploy of one file.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/contexts/AuthContext";
import { PageHeader, PageBody } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Check, X, ExternalLink, Video, Clipboard, Send, Loader2, Mic,
  ListChecks, AlertTriangle, FileText, Package,
} from "lucide-react";

function copy(text, label) {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copied`);
}

function CheckRow({ item }) {
  return (
    <div className="ap-panel-2 p-3 flex items-start justify-between gap-3"
         data-testid={`review-row-${item.key}`}>
      <div className="flex items-start gap-3 min-w-0 flex-1">
        {item.passed ? (
          <Check className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
        ) : (
          <X className="h-5 w-5 text-rose-400 shrink-0 mt-0.5" />
        )}
        <div className="min-w-0">
          <div className="text-sm text-zinc-200">{item.name}</div>
          {item.evidence && (
            <div className="text-[11px] mono text-zinc-500 mt-0.5 truncate" title={item.evidence}>
              {item.evidence}
            </div>
          )}
        </div>
      </div>
      {item.action_url && (
        <a href={item.action_url} target="_blank" rel="noreferrer"
           className="text-[11px] mono text-emerald-400 hover:text-emerald-300 shrink-0 inline-flex items-center gap-1">
          verify <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
  );
}

function ScopeJustificationRow({ scope, text }) {
  return (
    <div className="ap-panel-2 p-3 space-y-2" data-testid={`scope-row-${scope.replace(/\s|\+|\./g, "-")}`}>
      <div className="flex items-center justify-between gap-2">
        <code className="text-xs text-emerald-300 mono">{scope}</code>
        <button onClick={() => copy(text, `Justification for ${scope}`)}
                className="text-[10px] mono text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1"
                data-testid={`copy-scope-${scope.replace(/\s|\+|\./g, "-")}`}>
          <Clipboard className="h-3 w-3" /> copy
        </button>
      </div>
      <p className="text-[12px] text-zinc-300 leading-relaxed">{text}</p>
    </div>
  );
}

const FREE_TOOLS = [
  { name: "Loom", url: "https://loom.com", note: "Browser plugin, auto-uploads, instant shareable link." },
  { name: "QuickTime (macOS)", url: "https://support.apple.com/guide/quicktime-player/record-the-screen-qtp97b08e666/mac",
    note: "Built-in. File → New Screen Recording." },
  { name: "OBS Studio", url: "https://obsproject.com",
    note: "Free, cross-platform. Highest quality, steeper learning curve." },
  { name: "Win+G (Windows 11)", url: "https://support.microsoft.com/en-us/windows/record-a-clip-on-your-pc-using-game-bar-220c97c5-d8f4-4684-bb52-9b1c11b1a2b9",
    note: "Built-in Xbox Game Bar — press Win+G then the record button." },
  { name: "scrcpy", url: "https://github.com/Genymobile/scrcpy",
    note: "Mirror an Android device to your desktop for the phone segment." },
];

export default function ReviewReadinessPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [demoUrl, setDemoUrl] = useState("");
  const [demoNotes, setDemoNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [marking, setMarking] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/settings/review-readiness`);
      setData(r.data);
      setDemoUrl(r.data?.demo_video?.url || "");
      setDemoNotes(r.data?.demo_video?.notes || "");
    } catch (e) {
      toast.error("Failed to load review readiness");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/settings/review-readiness`);
        if (!mounted) return;
        setData(r.data);
        setDemoUrl(r.data?.demo_video?.url || "");
        setDemoNotes(r.data?.demo_video?.notes || "");
      } catch (e) {
        if (mounted) toast.error("Failed to load review readiness");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const saveDemo = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings/review-readiness/demo`,
        { url: demoUrl.trim(), notes: demoNotes.trim() });
      toast.success("Demo info saved");
      load();
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const markRecorded = async () => {
    setMarking(true);
    try {
      await axios.post(`${API}/settings/review-readiness/mark-recorded`);
      toast.success("Marked as recorded");
      load();
    } catch (e) {
      toast.error("Failed to mark recorded");
    } finally { setMarking(false); }
  };

  const score = data?.score || { passed: 0, total: 0, pct: 0, label: "" };
  const allClear = score.total > 0 && score.passed === score.total;
  const submission = data?.submission || {};
  const recording = data?.recording || {};
  const demoRecorded = Boolean(data?.demo_video?.recorded_at);

  return (
    <>
      <PageHeader
        title="Review Readiness"
        subtitle="Everything TikTok's review team will check before approving production scopes."
        icon={ListChecks}
        testid="review-readiness-page"
      />
      <PageBody>
        {/* Progress summary */}
        <div className="ap-panel p-4 mb-4 flex items-center gap-4" data-testid="review-progress">
          <div className="relative h-14 w-14 shrink-0">
            <svg viewBox="0 0 36 36" className="h-14 w-14 -rotate-90">
              <circle cx="18" cy="18" r="16" fill="none" stroke="#27272a" strokeWidth="3" />
              <circle cx="18" cy="18" r="16" fill="none"
                stroke={allClear ? "#10b981" : "#f59e0b"} strokeWidth="3"
                strokeDasharray={`${score.pct} 100`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-xs mono font-semibold text-zinc-200"
                 data-testid="review-progress-pct">
              {score.pct}%
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-zinc-100" data-testid="review-progress-label">
              {score.label}
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">
              {allClear
                ? "You're ready to submit for production review."
                : "Fix the red rows below, then record + upload your demo video."}
            </div>
          </div>
          <a href={submission.submission_portal || "https://developers.tiktok.com/apps"} target="_blank" rel="noreferrer"
             className={`px-3 py-2 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${
               allClear ? "bg-emerald-500 hover:bg-emerald-400 text-zinc-950"
                        : "bg-zinc-900 border border-zinc-700 text-zinc-400 hover:text-zinc-200"
             }`}
             data-testid="review-submit-btn">
            <Send className="h-3.5 w-3.5" />
            Open TikTok submission portal
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        {loading && <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />}

        {/* Checklist */}
        {data?.checks && (
          <div className="ap-panel p-4 space-y-2 mb-4" data-testid="review-checks">
            <h2 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-emerald-400" />
              Automatic checks
            </h2>
            {data.checks.map((c) => <CheckRow key={c.key} item={c} />)}
          </div>
        )}

        {/* Demo video form + Mark recorded */}
        <div className="ap-panel p-4 mb-4" data-testid="review-demo-form">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Video className="h-4 w-4 text-emerald-400" />
              <h2 className="text-sm font-semibold">Demo video</h2>
              {demoRecorded && (
                <Badge variant="outline" className="border-emerald-700/60 bg-emerald-500/10 text-emerald-300 text-[10px]">
                  recorded
                </Badge>
              )}
              {data?.demo_video?.url && (
                <Badge variant="outline" className="border-sky-700/60 bg-sky-500/10 text-sky-300 text-[10px]">
                  url saved
                </Badge>
              )}
            </div>
            <Button onClick={markRecorded} disabled={marking}
                    variant="outline"
                    className="border-zinc-700 bg-zinc-900 text-zinc-200 text-xs"
                    data-testid="review-mark-recorded-btn">
              {marking ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1.5" />}
              {demoRecorded ? "Re-mark recorded" : "Mark demo recorded"}
            </Button>
          </div>
          <div className="space-y-2">
            <label className="block">
              <span className="text-xs text-zinc-400">Loom / YouTube / direct MP4 URL</span>
              <Input
                value={demoUrl}
                onChange={(e) => setDemoUrl(e.target.value)}
                placeholder="https://loom.com/share/..."
                className="bg-zinc-900 border-zinc-700 text-zinc-100 mt-1"
                data-testid="review-demo-url-input"
              />
            </label>
            <label className="block">
              <span className="text-xs text-zinc-400">Notes (what you covered, timestamps)</span>
              <Textarea
                rows={2}
                value={demoNotes}
                onChange={(e) => setDemoNotes(e.target.value)}
                placeholder="0:00 login · 0:30 connect TikTok · 1:00 generate · 1:45 post"
                className="bg-zinc-900 border-zinc-700 text-zinc-100 mt-1"
                data-testid="review-demo-notes-input"
              />
            </label>
            <Button onClick={saveDemo} disabled={saving}
                    className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold"
                    data-testid="review-demo-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              Save demo info
            </Button>
          </div>
        </div>

        {/* Submission packet — copy-paste ready */}
        <div className="ap-panel p-4 mb-4" data-testid="review-submission-packet">
          <div className="flex items-center gap-2 mb-3">
            <Package className="h-4 w-4 text-emerald-400" />
            <h2 className="text-sm font-semibold">Submission packet</h2>
            <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px] mono">copy-paste ready</Badge>
          </div>

          <div className="grid md:grid-cols-2 gap-3 mb-4">
            <div className="ap-panel-2 p-3">
              <div className="text-[10px] mono uppercase text-zinc-500 mb-1">Privacy policy URL</div>
              <div className="flex items-center gap-2">
                <code className="text-xs text-zinc-200 mono truncate flex-1"
                      data-testid="submission-privacy-url">
                  {submission.privacy_url || "—"}
                </code>
                <button onClick={() => copy(submission.privacy_url, "Privacy URL")}
                        className="text-[10px] mono text-emerald-400 hover:text-emerald-300"
                        data-testid="copy-privacy-url">
                  <Clipboard className="h-3 w-3" />
                </button>
              </div>
            </div>
            <div className="ap-panel-2 p-3">
              <div className="text-[10px] mono uppercase text-zinc-500 mb-1">Terms URL</div>
              <div className="flex items-center gap-2">
                <code className="text-xs text-zinc-200 mono truncate flex-1"
                      data-testid="submission-terms-url">
                  {submission.terms_url || "—"}
                </code>
                <button onClick={() => copy(submission.terms_url, "Terms URL")}
                        className="text-[10px] mono text-emerald-400 hover:text-emerald-300"
                        data-testid="copy-terms-url">
                  <Clipboard className="h-3 w-3" />
                </button>
              </div>
            </div>
            <div className="ap-panel-2 p-3 md:col-span-2">
              <div className="text-[10px] mono uppercase text-zinc-500 mb-1">Demo video URL</div>
              <div className="flex items-center gap-2">
                <code className="text-xs text-zinc-200 mono truncate flex-1"
                      data-testid="submission-demo-url">
                  {submission.demo_video_url || "(save the URL above and refresh)"}
                </code>
                {submission.demo_video_url && (
                  <button onClick={() => copy(submission.demo_video_url, "Demo URL")}
                          className="text-[10px] mono text-emerald-400 hover:text-emerald-300"
                          data-testid="copy-demo-url">
                    <Clipboard className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <h3 className="text-xs uppercase mono text-zinc-500 mb-2 flex items-center gap-1.5">
            <FileText className="h-3 w-3" /> Scope justifications (paste into TikTok app review portal)
          </h3>
          <div className="space-y-2">
            {(submission.scope_justifications || []).map((s) => (
              <ScopeJustificationRow key={s.scope} scope={s.scope} text={s.text} />
            ))}
          </div>
        </div>

        {/* Demo Recording Guide */}
        <div className="ap-panel p-4" data-testid="review-recording-guide">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Mic className="h-4 w-4 text-rose-400" />
              <h2 className="text-sm font-semibold">Demo recording guide</h2>
              <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px] mono">2–3 min target</Badge>
            </div>
            <button onClick={() => copy(recording.voiceover_script || "", "Voiceover script")}
                    className="text-[11px] mono text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1"
                    data-testid="copy-voiceover-btn">
              <Clipboard className="h-3 w-3" /> copy voiceover
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xs uppercase mono text-zinc-500 mb-2">8-step recording flow</h3>
              <ol className="space-y-2">
                {(recording.steps || []).map((s) => (
                  <li key={s.n} className="flex gap-2.5 items-start"
                      data-testid={`recording-step-${s.n}`}>
                    <span className="h-5 w-5 rounded-full bg-emerald-500/15 border border-emerald-700/60 text-emerald-300 text-[10px] mono flex items-center justify-center shrink-0 mt-0.5">
                      {s.n}
                    </span>
                    <div>
                      <div className="text-xs font-medium text-zinc-200">{s.title}</div>
                      <div className="text-[11px] text-zinc-500 leading-relaxed">{s.detail}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <div>
              <h3 className="text-xs uppercase mono text-zinc-500 mb-2">Voiceover script (read aloud)</h3>
              <div className="ap-panel-2 p-3 text-xs text-zinc-300 whitespace-pre-line leading-relaxed max-h-[420px] overflow-y-auto"
                   data-testid="voiceover-text">
                {recording.voiceover_script || "Loading…"}
              </div>

              <h3 className="text-xs uppercase mono text-zinc-500 mt-4 mb-2">Recording tips</h3>
              <ul className="space-y-1 text-[11px] text-zinc-400" data-testid="recording-tips">
                {(recording.tips || []).map((t, i) => (
                  <li key={i} className="flex gap-1.5">
                    <span className="text-emerald-400 shrink-0">•</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>

              <h3 className="text-xs uppercase mono text-zinc-500 mt-4 mb-2">Free recording tools</h3>
              <ul className="space-y-1.5">
                {FREE_TOOLS.map((t) => (
                  <li key={t.name} className="text-[11px] text-zinc-400">
                    <a href={t.url} target="_blank" rel="noreferrer"
                       className="text-emerald-400 hover:text-emerald-300 font-medium">
                      {t.name}
                    </a>{" "}— {t.note}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {!allClear && (
          <div className="mt-4 ap-panel p-3 flex items-start gap-2 border border-amber-700/40"
               data-testid="review-incomplete-alert">
            <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-200">
              You can still <span className="font-semibold">open the TikTok submission portal</span> with some
              gates red, but TikTok will reject the submission. Fix the failing rows first.
            </div>
          </div>
        )}
      </PageBody>
    </>
  );
}
